/**
 * プロダクトリソース関連の処理
 * ドメインに基づいてプロダクトを判定し、タイトルやfaviconを適用する
 */

// =============== 型定義 ===============

/** prod-info.jsonの型定義 */
export interface ProdInfo {
  ProductName?: string;
  ProductCode?: string;
}

/** プロダクト情報の型定義 */
export interface ProductInfo {
  productName: string;
  domain: string;
  prodInfoPath: string;
  faviconPath: string;
  logoPath: string;
}

// =============== ヘルパー関数 ===============

/**
 * 現在のドメインを取得（サブドメイン除去）
 */
export const getDomain = (): string => {
  const hostname = window.location.hostname;
  if (/^\d+\.\d+\.\d+\.\d+$/.test(hostname)) return hostname;
  if (hostname === "localhost") return hostname;
  const parts = hostname.split(".");
  if (parts.length > 2) return parts.slice(-2).join(".");
  return hostname;
};

/**
 * プロダクト名を判定
 * EVECLOUDドメインに含まれるかどうかで判定
 */
export const detectProductName = (): string => {
  const evecloudDomains = (import.meta.env.VITE_EVECLOUD_DOMAINS || "")
    .split(",")
    .map((d: string) => d.trim())
    .filter((d: string) => d.length > 0);

  return evecloudDomains.includes(getDomain()) ? "EVECLOUD" : "Cloud UniPass";
};

/**
 * プロダクト情報を取得
 */
const getProductInfo = (): ProductInfo => {
  const productName = detectProductName();
  const domain = getDomain();

  return {
    productName,
    domain,
    prodInfoPath: `/assets/product_resources/${productName}/prod-info.json`,
    faviconPath: `/assets/product_resources/${productName}/favicon.ico`,
    logoPath: `/assets/product_resources/${productName}/logo.png`,
  };
};

/**
 * 静的ファイルからprod-info.jsonを読み込む
 */
const loadProdInfo = async (prodInfoPath: string): Promise<ProdInfo | null> => {
  try {
    const response = await fetch(`${prodInfoPath}?v=${Date.now()}`, {
      cache: "no-store",
    });
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    console.warn("Failed to load prod-info.json:", error);
  }
  return null;
};

/**
 * プロダクトリソースを適用（タイトル、favicon）
 */
const applyProductResources = (
  prodInfo: ProdInfo | null,
  faviconPath: string,
  systemName: string,
): void => {
  // プロダクト名をタイトルに適用
  if (prodInfo?.ProductName && prodInfo.ProductName.trim().length > 0) {
    document.title = `${prodInfo.ProductName.trim()} ${systemName}`;
  }

  // faviconを適用（静的ファイル）
  let linkEl = document.querySelector(
    "link[rel*='icon']",
  ) as HTMLLinkElement | null;
  if (!linkEl) {
    linkEl = document.createElement("link") as HTMLLinkElement;
    linkEl.rel = "icon";
    document.head.appendChild(linkEl);
  }
  linkEl.href = `${faviconPath}?v=${Date.now()}`;
};

/**
 * ロゴパスを取得
 */
export const getLogoPath = (): string => {
  const productInfo = getProductInfo();
  return productInfo.logoPath;
};

/**
 * デフォルトロゴパスを取得（エラー時のフォールバック用）
 */
export const getDefaultLogoPath = (): string => {
  return "/logo.png";
};

/**
 * プロダクトリソースを初期化
 * main.tsから呼び出すエントリーポイント
 */
export const initProductResources = async (
  systemName: string,
): Promise<ProdInfo | null> => {
  const productInfo = getProductInfo();

  console.log(
    `Product detected: ${productInfo.productName} (domain: ${productInfo.domain})`,
  );

  // 静的ファイルからプロダクト情報を読み込み
  const prodInfo = await loadProdInfo(productInfo.prodInfoPath);

  // プロダクトリソースを適用
  applyProductResources(prodInfo, productInfo.faviconPath, systemName);

  return prodInfo;
};
